import { HotelImageEntry } from "@/lib/Types/HotelInfoResponse/HotelInfoResponse";
import { GuestPicturesResponse } from "@/lib/Types/GuestPicturesResponse";

// Top-level gallery tabs
export enum GalleryTab {
  ALL = "ALL",
  HOTEL = "HOTEL",
  GUEST = "GUEST",
  VIDEO = "VIDEO",
}

// German labels for gallery tabs
export const TAB_LABELS: Record<GalleryTab, string> = {
  [GalleryTab.ALL]: "Alle Bilder",
  [GalleryTab.HOTEL]: "Hotelbilder",
  [GalleryTab.GUEST]: "Gästebilder",
  [GalleryTab.VIDEO]: "Hotelvideo",
};

// Image category enum (VIDEO removed - handled in separate tab)
export enum ImageCategory {
  ALL = "ALL",
  HOTEL = "HOTEL",
  ROOM = "ROOM",
  LOBBY = "LOBBY",
  BEACH = "BEACH",
  RESTAURANTS = "RESTAURANTS",
  POOLS = "POOLS",
  WELLNESS = "WELLNESS",
  SPORT = "SPORT",
  GUEST = "GUEST",
  MISCELLANEOUS = "MISCELLANEOUS",
}

// Unified media item type (images only - videos handled separately)
export interface GalleryMediaItem {
  id: string;
  url: string;
  type: "hotel" | "guest";
  category: ImageCategory;
  caption?: string;
  imageType?: string; // Original type code (A, AN, W, etc.)
}

// Category filter option for UI
export interface CategoryFilterOption {
  category: ImageCategory;
  label: string;
  count: number;
}

// Category to image type mappings
const CATEGORY_TYPE_MAP: Record<ImageCategory, string[]> = {
  [ImageCategory.ALL]: [],
  [ImageCategory.HOTEL]: ["A", "AN"],
  [ImageCategory.ROOM]: ["W", "ZI"],
  [ImageCategory.LOBBY]: ["EI", "L", "TE"],
  [ImageCategory.BEACH]: ["S"],
  [ImageCategory.RESTAURANTS]: ["BA", "SB", "IT", "PB", "R"],
  [ImageCategory.POOLS]: ["HB", "P", "PO"],
  [ImageCategory.WELLNESS]: ["FR"],
  [ImageCategory.SPORT]: ["SK", "SP"],
  [ImageCategory.GUEST]: [],
  [ImageCategory.MISCELLANEOUS]: [],
};

// German labels for categories
export const CATEGORY_LABELS: Record<ImageCategory, string> = {
  [ImageCategory.ALL]: "Alle Bilder",
  [ImageCategory.HOTEL]: "Hotel",
  [ImageCategory.ROOM]: "Zimmer",
  [ImageCategory.LOBBY]: "Lobby / Terrasse",
  [ImageCategory.BEACH]: "Strand",
  [ImageCategory.RESTAURANTS]: "Restaurants / Bars",
  [ImageCategory.POOLS]: "Pools",
  [ImageCategory.WELLNESS]: "Wellness / Fitness",
  [ImageCategory.SPORT]: "Sport",
  [ImageCategory.GUEST]: "Gästefotos",
  [ImageCategory.MISCELLANEOUS]: "Sonstiges",
};

// Get category from image type code
function getCategoryFromType(type: string): ImageCategory {
  for (const [category, types] of Object.entries(CATEGORY_TYPE_MAP)) {
    if (types.includes(type)) {
      return category as ImageCategory;
    }
  }
  return ImageCategory.MISCELLANEOUS;
}

// Normalize all media items into unified format (images only - videos handled separately)
export function normalizeMediaItems(
  hotelImageEntries: HotelImageEntry[],
  guestPictures: GuestPicturesResponse | null
): GalleryMediaItem[] {
  const items: GalleryMediaItem[] = [];

  // Add hotel images
  hotelImageEntries.forEach((entry, index) => {
    items.push({
      id: `hotel-${index}`,
      url: entry.image.url,
      type: "hotel",
      category: getCategoryFromType(entry.image.type),
      imageType: entry.image.type,
      caption: `@ ${entry.image?.operatorCode || undefined}`,
    });
  });

  // Add guest pictures
  if (guestPictures?.guestPictures) {
    guestPictures.guestPictures.forEach((picture, index) => {
      items.push({
        id: `guest-${index}`,
        url: picture.path,
        type: "guest",
        category: ImageCategory.GUEST,
        caption: picture.username || undefined,
      });
    });
  }

  return items;
}

// Get available categories with counts (only categories that have items)
export function getAvailableCategories(
  items: GalleryMediaItem[]
): CategoryFilterOption[] {
  const categoryCounts = new Map<ImageCategory, number>();

  // Count items per category
  items.forEach((item) => {
    const count = categoryCounts.get(item.category) || 0;
    categoryCounts.set(item.category, count + 1);
  });

  // Build filter options for categories with items
  const options: CategoryFilterOption[] = [];

  // Always add "All" first
  options.push({
    category: ImageCategory.ALL,
    label: CATEGORY_LABELS[ImageCategory.ALL],
    count: items.length,
  });

  // Add categories in a specific order (GUEST excluded - has dedicated tab)
  const categoryOrder: ImageCategory[] = [
    ImageCategory.HOTEL,
    ImageCategory.ROOM,
    ImageCategory.LOBBY,
    ImageCategory.BEACH,
    ImageCategory.RESTAURANTS,
    ImageCategory.POOLS,
    ImageCategory.WELLNESS,
    ImageCategory.SPORT,
    ImageCategory.MISCELLANEOUS,
  ];

  categoryOrder.forEach((category) => {
    const count = categoryCounts.get(category);
    if (count && count > 0) {
      options.push({
        category,
        label: CATEGORY_LABELS[category],
        count,
      });
    }
  });

  return options;
}

// Filter items by category
export function filterByCategory(
  items: GalleryMediaItem[],
  category: ImageCategory
): GalleryMediaItem[] {
  if (category === ImageCategory.ALL) {
    return items;
  }
  return items.filter((item) => item.category === category);
}
